import { useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import type { TaxonomyTag } from '@/types/domain';
import { strings } from '@/strings';

const setTaxonomyTag = callable<{ tagId: string; active: boolean }, { tagId: string }>('setTaxonomyTag');
const seedTaxonomy = callable<Record<string, never>, { taxonomy: number; causeAreas: number }>('seedTaxonomy');

type Tag = TaxonomyTag & { id: string };

/**
 * The shared vocabulary both sides of a match are described in.
 *
 * Grouped by domain because the tree's shape is the point: "volunteers" splits
 * into inspiration and operations precisely because an organization that has
 * one usually lacks the other, and a flat list would hide that.
 *
 * A retired tag stays visible, marked. It is still referenced by existing
 * candidate competencies, opportunity requirements and assessment answers, and
 * hiding it would make those records look like they point at nothing.
 */
export function TaxonomyPage() {
  const { rows, loading, error } = useDocs<TaxonomyTag>(
    query(collection(db, 'taxonomy'), orderBy('order', 'asc')),
    []
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  const domains = rows.filter((row) => !row.parentId);
  const childrenOf = (domainId: string) => rows.filter((row) => row.parentId === domainId);

  async function toggle(tagId: string, active: boolean) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await setTaxonomyTag({ tagId, active });
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function reseed() {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setSeeded(false);
    try {
      await seedTaxonomy({});
      setSeeded(true);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  function tagRow(tag: Tag) {
    const retired = tag.active === false;
    return (
      <li
        key={tag.id}
        data-testid={`tag-${tag.id}`}
        className="flex items-center justify-between gap-3 rounded-md px-3 py-2 hover:bg-surface"
      >
        <span className="flex flex-wrap items-center gap-2 text-sm text-ink">
          {tag.nameAr}
          {retired ? (
            <span className="rounded-md bg-surface px-2 py-0.5 text-xs text-muted">{strings.taxonomyInactive}</span>
          ) : null}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => toggle(tag.id, retired)}
          className="min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
        >
          {retired ? strings.taxonomyActivate : strings.taxonomyDeactivate}
        </button>
      </li>
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-primary">{strings.taxonomyTitle}</h1>
        <button
          type="button"
          disabled={busy}
          onClick={reseed}
          className="min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
        >
          {strings.taxonomyReseed}
        </button>
      </div>

      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && rows.length === 0 ? <p className="text-muted">{strings.taxonomyEmpty}</p> : null}
      {seeded ? <p className="text-sm text-success">{strings.taxonomyReseeded}</p> : null}
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}

      <div className="flex flex-col gap-4">
        {domains.map((domain) => (
          <section
            key={domain.id}
            data-testid={`domain-${domain.id}`}
            className="rounded-lg border border-border-subtle bg-white p-4"
          >
            <h2 className="mb-2 font-semibold text-ink">{domain.nameAr}</h2>
            <ul className="flex flex-col">{childrenOf(domain.id).map(tagRow)}</ul>
          </section>
        ))}
      </div>
    </div>
  );
}
